import { LoadingButton, LoadingButtonProps } from "@mui/lab";
import type { SystemStyleObject } from "@mui/system";

import React from "react";

import { Theme, useTheme } from "@mui/material";
import { darken } from "@mui/material/styles";
import type { CSSObject } from "@mui/material/styles";

import { useGoogleTranslateDetection } from "./googleTranslate";

/** Compact pill geometry for the admin shell chrome (toolbars, dialogs). */
export type AdminButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export type StyledButtonProps = Omit<LoadingButtonProps, "children"> & {
  /** the text value for the button */
  label: string | React.ReactNode;
  /** updates the style theme to match the grey button found in the design */
  isGreyStyle?: boolean;
  /** updates the style theme to match the grey button found in the design */
  isDeleteStyle?: boolean;
  /** Design sometimes require the button to be thinner than MUI design which is done by height fixing. */
  thin?: boolean;
  /** Design button based on new design system */
  isOscarDesign?: boolean;
  /** Renders the compact admin-shell pill styling for the given variant. */
  adminVariant?: AdminButtonVariant;
};

export function adminButtonSx(
  theme: Theme,
  variant: AdminButtonVariant
): CSSObject {
  const { palette } = theme;
  const base: CSSObject = {
    ...theme.typography.labelRMedium,
    height: "36px",
    padding: "0 14px",
    borderRadius: "999px",
    minWidth: 0,
    gap: "8px",
    flexShrink: 0,
    whiteSpace: "nowrap",
    textTransform: "none",
    transition: "background-color 120ms, box-shadow 120ms, color 120ms",
  };

  switch (variant) {
    case "primary":
      return {
        ...base,
        backgroundColor: palette.secondary.main,
        color: palette.common.white,
        "&:hover": { backgroundColor: darken(palette.secondary.main, 0.15) },
        "&.Mui-disabled": {
          backgroundColor: palette.grey[300],
          color: palette.grey[700],
          opacity: 1,
        },
      };
    case "ghost":
      return {
        ...base,
        backgroundColor: "transparent",
        color: palette.grey[700],
        "&:hover": {
          backgroundColor: palette.grey[100],
          color: palette.grey[900],
        },
      };
    case "danger":
      return {
        ...base,
        backgroundColor: palette.grey[50],
        color: palette.error.dark,
        border: `1px solid ${palette.grey[300]}`,
        "&:hover": { backgroundColor: palette.grey[100] },
      };
    case "secondary":
    default:
      return {
        ...base,
        backgroundColor: palette.grey[50],
        color: palette.grey[900],
        border: `1px solid ${palette.grey[300]}`,
        "&:hover": { backgroundColor: palette.grey[100] },
      };
  }
}

/** This button is a styled MUI Button where all the Button props are passed through. There are two additional values:
 * <p> First, the button label which is the text for the button
 * <p> Second is `isGreyStyle` which updates the style theme to match the grey button found in the design*/
export const StyledButton = ({
  label,
  isGreyStyle = false,
  isDeleteStyle = false,
  thin = false,
  isOscarDesign,
  adminVariant,
  sx,
  loading,
  ...props
}: StyledButtonProps) => {
  const isGoogleTranslateActive = useGoogleTranslateDetection();
  const hasReadableLabel = typeof label === "string" && label.trim().length > 0;

  const { typography } = useTheme();
  const sxArray =
    sx === undefined
      ? []
      : sx instanceof Array
      ? sx
      : ([sx] as ReadonlyArray<
          | boolean
          | SystemStyleObject<Theme>
          | ((theme: Theme) => SystemStyleObject<Theme>)
        >);

  const ownSx = [thin ? { py: isOscarDesign ? 0 : 0.5 } : {}] as ReadonlyArray<
    SystemStyleObject<Theme>
  >;

  const greyButtonSx = () =>
    [
      ({ palette }) => ({
        color: palette.grey.A700,
        backgroundColor:
          props.variant === "text" ? palette.grey[50] : palette.grey.A200,
        border: "none",
        "&:hover": {
          backgroundColor: palette.grey.A100,
          border: "none",
        },
        "&:disabled": {
          border: "none",
        },
      }),
    ] as ReadonlyArray<(theme: Theme) => SystemStyleObject<Theme>>;

  const deleteButtonSx = () =>
    [
      ({ palette }) => ({
        color: palette.common.white,
        backgroundColor: palette.error.light,
        border: "none",
        "&:hover": {
          backgroundColor: palette.error.dark,
          border: "none",
        },
        "&:disabled": {
          border: "none",
        },
      }),
    ] as ReadonlyArray<(theme: Theme) => SystemStyleObject<Theme>>;

  return (
    <LoadingButton
      disableElevation={true}
      type="button"
      // Only name the button when its label cannot be read out. Text is already
      // the accessible name, and overriding it with "delete" lost both the word
      // on screen and, on a button reading "Remove", the verb itself. Props
      // spread below, so a caller can still pass something more specific.
      aria-label={isDeleteStyle && !hasReadableLabel ? "delete" : undefined}
      sx={[
        ...(isOscarDesign
          ? [typography.buttonL as SystemStyleObject<Theme>]
          : []),
        ...sxArray,
        ...ownSx,
        ...(isGreyStyle ? greyButtonSx() : []),
        ...(isDeleteStyle ? deleteButtonSx() : []),
        ...(adminVariant
          ? [(theme: Theme) => adminButtonSx(theme, adminVariant)]
          : []),
      ]}
      // Google Translate is not supported for loading state
      loading={isGoogleTranslateActive ? false : loading}
      {...props}
    >
      {label}
    </LoadingButton>
  );
};
